import React from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useCompanies } from "../../api/queries/organizationQueries";
import { setWorkingCompany } from "../../store/slices/authSlice";
import useCurrentUser from "../../hooks/useCurrentUser";

const EMPTY = [];

/**
 * Accounts with no company (the super admin) see every company. "All companies"
 * (the default) shows lists and dashboards across companies; picking one narrows
 * them and is used as "my company" by create forms and per-company setup screens.
 */
export const WorkingCompanySelect = () => {
  const user = useCurrentUser();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { data: companies = EMPTY } = useCompanies();
  if (!user.noCompany) return null;

  const change = (id) => {
    const company = companies.find((c) => c.id === id);
    dispatch(
      setWorkingCompany({
        id: id || null,
        name: company ? company.companyName || company.name : null,
      }),
    );
    // Everything cached belongs to the previous company
    queryClient.resetQueries({
      predicate: (q) => q.queryKey[0] !== "permissions",
    });
  };

  return (
    <div className="d-flex align-items-center gap-2 me-3">
      <label
        htmlFor="working-company"
        className="fs-070 fw-semibold text-uppercase text-slate-500 d-none d-lg-inline"
      >
        Company
      </label>
      <select
        id="working-company"
        className="form-select form-select-sm"
        value={user.companyId || ""}
        onChange={(e) => change(e.target.value)}
      >
        <option value="">All companies</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.companyName || c.name}
          </option>
        ))}
      </select>
    </div>
  );
};

/** True for a no-company account (super admin) viewing "All companies". */
export const useAllCompanies = () => {
  const user = useCurrentUser();
  return !!user.noCompany && !user.companyId;
};

/**
 * Inline hint where one specific company is needed (create forms, per-company
 * setup) while "All companies" is selected. Renders nothing otherwise.
 */
export const NeedCompanyHint = ({ action = "use this", className = "" }) => {
  const allCompanies = useAllCompanies();
  if (!allCompanies) return null;
  return (
    <div className={`alert alert-info fs-080 py-2 ${className}`} role="status">
      <i className="bi bi-buildings me-2"></i>Pick a company at the top right to{" "}
      {action}.
    </div>
  );
};
